import { useTranslation } from "react-i18next";
import type { TimeAttendanceStepProps } from "@/types/timeAttendance";
import {
  Camera,
  ChevronRight,
  CircleCheck,
  Lock,
  MapPin,
  TriangleAlert,
} from "lucide-react";
import React from "react";
import { Field, FieldLabel } from "../ui/field";
import { Textarea } from "../ui/textarea";
import { ErrorMessage } from "../ErrorMessage";
import { Button } from "../ui/button";
import { startCase } from "lodash";
import { ENUM_ATTENDANCE_REQUEST_TYPE } from "@/utils/enums/time-attendance.enum";

const TimeAttendanceRuleStep2: React.FC<TimeAttendanceStepProps> = ({
  onNext,
  onReject,
  onStartLocation,
  onStartPhoto,
  completedSteps = { location: false, photo: false },
  eligibility,
  formik,
  isSubmitting,
  withoutLocation,
}) => {
  const { t } = useTranslation(["common", "my-space"]);
  const requestTypeLabel =
    eligibility?.requestType === ENUM_ATTENDANCE_REQUEST_TYPE.CHECK_OUT
      ? t("my-space:time_attendance.check_out")
      : eligibility?.requestType === ENUM_ATTENDANCE_REQUEST_TYPE.CHECK_IN
        ? t("my-space:time_attendance.check_in")
        : startCase(eligibility?.requestType)?.replace(" ", "-");
  const isLocationVerified = eligibility?.location?.isVerified;
  const needsAdminApproval =
    !isLocationVerified && eligibility?.location?.userLocation?.latitude;
  const isLocationAccessRequired =
    !isLocationVerified &&
    !eligibility?.location?.userLocation?.latitude &&
    withoutLocation;
  const isPhotoRequired =
    Object.hasOwn(
      eligibility?.timeAttendanceRuleSummary,
      "isCapturePhotoCheckinCheckout",
    ) && !eligibility?.timeAttendanceRuleSummary.isCapturePhotoCheckinCheckout;
  const isLocationRequired =
    Object.hasOwn(
      eligibility?.timeAttendanceRuleSummary,
      "isLocationMandatoryCheckinCheckout",
    ) || eligibility?.empAttendanceRulesSummary?.method;
  const isPhotoDisabled =
    !completedSteps.location &&
    !eligibility?.location?.userLocation?.latitude &&
    isLocationRequired;

  const canContinue =
    (completedSteps.location ||
      eligibility?.location?.userLocation?.latitude ||
      isLocationAccessRequired ||
      !isLocationRequired) &&
    (completedSteps.photo || !isPhotoRequired) &&
    (!needsAdminApproval || formik?.values.requestReason?.trim());

  return (
    <div className="p-6">
      <div
        className={`text-xs px-4 py-3 rounded-xs mb-6 flex items-center justify-center gap-2 ${
          isLocationAccessRequired
            ? "bg-red-100 text-red-600"
            : !needsAdminApproval && canContinue
              ? "bg-green-100 text-green-600"
              : "bg-orange-100 text-orange-600"
        }`}
      >
        <div className="shrink-0">
          {!needsAdminApproval && canContinue ? (
            <CircleCheck size={18} />
          ) : (
            <TriangleAlert size={18} />
          )}
        </div>
        <p className="flex-1 leading-snug">
          {isLocationAccessRequired || needsAdminApproval
            ? t("my-space:checkin.location_doesnt_match_submit_request")
            : canContinue
              ? t("my-space:checkin.all_actions_match_can_check_in")
              : t("my-space:checkin.provide_missing_rules_to_check_in")}
        </p>
      </div>

      {/* Required Actions Title */}
      <p className="text-xs text-gray-500 mb-3">
        {t("my-space:checkin.required_actions_caps_colon")}
      </p>

      {/* Location Card */}
      {isLocationRequired && (
        <div
          className={`border rounded-xs p-2 mb-3 flex justify-between items-center transition-all ${
            isLocationAccessRequired
              ? "border-red-400 bg-red-50"
              : needsAdminApproval
                ? "border-orange-400 bg-orange-50"
                : completedSteps.location || isLocationVerified
                  ? "border-green-400 bg-green-50"
                  : ""
          } ${eligibility?.location?.userLocation?.latitude ? "cursor-pointer hover:shadow-md" : ""}`}
          onClick={() => {
            if (eligibility?.location?.userLocation?.latitude) {
              onStartLocation?.();
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-1.5 border rounded-full ${
                isLocationAccessRequired
                  ? "border-red-400 bg-red-100"
                  : needsAdminApproval
                    ? "border-orange-400 bg-orange-100"
                    : completedSteps.location || isLocationVerified
                      ? "border-green-400 bg-green-100"
                      : ""
              }`}
            >
              <MapPin
                className={`${
                  isLocationAccessRequired
                    ? "text-red-600"
                    : needsAdminApproval
                      ? "text-orange-600"
                      : completedSteps.location || isLocationVerified
                        ? "text-green-600"
                        : "text-gray-500"
                }`}
                size={18}
              />
            </div>
            <div>
              <p className="font-semibold text-sm">
                {t("my-space:checkin.share_location")}
              </p>
              <p
                className={`text-xs ${isLocationAccessRequired ? "text-red-600" : "text-gray-500"}`}
              >
                {isLocationVerified
                  ? t("my-space:checkin.location_verified_successfully")
                  : eligibility?.location?.userLocation?.latitude
                    ? t("my-space:checkin.location_outside_allowed_radius")
                    : t("my-space:checkin.verify_checkin_location")}
              </p>
            </div>
          </div>
          {!eligibility?.location?.userLocation?.latitude ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStartLocation?.();
              }}
              className={`flex gap-1 items-center ${isLocationAccessRequired ? "text-red-600" : "text-indigo-600"} font-medium text-sm hover:text-indigo-700`}
            >
              {isLocationAccessRequired ? (
                <span className="text-xs">
                  {t("my-space:checkin.enable_location")}
                </span>
              ) : (
                <span>{t("my-space:checkin.start")}</span>
              )}
              <ChevronRight size={18} className="rtl-flip" />
            </button>
          ) : (
            <></>
          )}
          {eligibility?.location?.userLocation?.latitude ? (
            <div
              className={`flex items-center gap-1 ${isLocationVerified ? "text-green-600" : "text-orange-600"} text-sm font-medium`}
            >
              <CircleCheck strokeWidth={1.5} />
            </div>
          ) : (
            <></>
          )}
        </div>
      )}

      {/* Photo Card */}
      {isPhotoRequired && (
        <div
          className={`border rounded-xs p-2 mb-6 flex justify-between items-center transition-all  ${
            completedSteps.photo
              ? "border-green-400 bg-green-50 cursor-pointer hover:shadow-md"
              : isPhotoDisabled
                ? "bg-gray-50 opacity-60"
                : ""
          }`}
          onClick={() => {
            if (completedSteps.photo) {
              onStartPhoto?.();
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-1.5 border rounded-full ${
                completedSteps.photo
                  ? "border-green-400 bg-green-100"
                  : isPhotoDisabled
                    ? "border-gray-300 bg-gray-100"
                    : ""
              }`}
            >
              {isPhotoDisabled ? (
                <Lock className="text-gray-400" size={18} />
              ) : (
                <Camera
                  className={` ${
                    completedSteps.photo
                      ? "text-green-400"
                      : isPhotoDisabled
                        ? "text-gray-400"
                        : ""
                  }`}
                  size={18}
                />
              )}
            </div>
            <div>
              <p className="font-semibold text-sm">
                {t("my-space:checkin.selfie_photo_verification")}
              </p>
              <p className="text-xs text-gray-500">
                {completedSteps.photo
                  ? t("my-space:checkin.photo_captured_successfully")
                  : isPhotoDisabled
                    ? t("my-space:checkin.complete_location_sharing_first")
                    : t("my-space:checkin.required_to_verify_presence")}
              </p>
            </div>
          </div>

          {!completedSteps.photo && !isPhotoDisabled && (
            <button
              onClick={onStartPhoto}
              className="flex gap-1 items-center text-indigo-600 font-medium text-sm hover:text-indigo-700"
            >
              <span>{t("my-space:checkin.start")}</span>
              <ChevronRight size={18} className="rtl-flip" />
            </button>
          )}

          {!completedSteps.photo && isPhotoDisabled && (
            <div className="flex items-center gap-1 text-gray-400 text-sm font-medium">
              <Lock size={18} />
            </div>
          )}
          {completedSteps.photo && (
            <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <CircleCheck strokeWidth={1.5} />
            </div>
          )}
        </div>
      )}

      {/* Reason Textarea - Only show when location is not verified */}
      {(needsAdminApproval || isLocationAccessRequired) && formik && (
        <Field className="mb-6">
          <FieldLabel>{t("my-space:checkin.reason_caps_required")}</FieldLabel>
          <Textarea
            className="min-h-24"
            name="requestReason"
            value={formik.values.requestReason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t(
              "common:placeholders.enter_the_details_of_your_reason_for_check_in",
            )}
          />
          {formik.touched.requestReason && formik.errors.requestReason && (
            <ErrorMessage
              text={formik.errors.requestReason}
              className="text-sm!"
              wrapperClassName="items-start! justify-start! p-0! h-auto!"
            />
          )}
        </Field>
      )}

      {/* Continue/Submit Button */}
      <Button
        disabled={!canContinue || isSubmitting}
        onClick={onNext}
        className={`w-full py-2 rounded-xs font-semibold mb-3 transition ${
          canContinue && !isSubmitting
            ? "bg-indigo-600 text-white hover:bg-indigo-700"
            : "bg-indigo-200 text-white cursor-not-allowed"
        }`}
      >
        {isSubmitting
          ? t("common:qr_upload.submitting")
          : needsAdminApproval
            ? t("my-space:checkin.submit_for_admin_approval")
            : requestTypeLabel}
      </Button>

      {/* Cancel Button */}
      <Button
        onClick={() => onReject?.("User cancelled")}
        className="w-full py-2 rounded-xs border text-gray-600 bg-gray-50 hover:bg-gray-100"
        disabled={isSubmitting}
      >
        {t("my-space:checkin.cancel_type", { type: requestTypeLabel })}
      </Button>
    </div>
  );
};

export default TimeAttendanceRuleStep2;
